import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PulseLoader from "./Utility/PulseLoader";
import { FiPhone, FiMapPin, FiCalendar } from "react-icons/fi";
import InfiniteScroll from "react-infinite-scroll-component";

function DoctorsGrid({
  isLoading,
  isSuccess,
  isFetching,
  getDoctors,
  selectedFilters,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [doctors, setDoctors] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  function convertFilters(selectedFilters) {
    if (!selectedFilters) return [];
    return Object.entries(selectedFilters).map(([key, valueSet]) => ({
      filterName: key,
      filterOperator: valueSet.operator,
      filterValue: Array.from(valueSet.values).join(", "),
    }));
  }

  const fetchGridData = async (offset) => {
    try {
      const pageSize = 5;
      const filters = convertFilters(selectedFilters);
      const result = await getDoctors(offset, filters);
      

      if (Array.isArray(result)) {
        setDoctors((prev) => (offset === 1 ? result : [...prev, ...result]));
        setHasMore(result.length === pageSize);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setHasMore(false);
    }
  };

  const fetchNextData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
  };

  useEffect(() => {
    setDoctors([]);
    setPage(1);
    setHasMore(true);
    fetchGridData(1); 
  }, [selectedFilters]);

useEffect(() => {
  if (hasMore) {
    fetchGridData(page);
  }
}, [page]);

  useEffect(() => {
 
}, [selectedFilters]);



  const getStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "available":
        return "bg-green-100 text-green-800 border-green-200";
      case "busy":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "on leave":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusDotColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "available":
        return "bg-green-500";
      case "busy":
        return "bg-yellow-500";
      case "on leave":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getInitialsBg = (index) => {
    const colors = [
      "linear-gradient(135deg, #667eea, #764ba2)",
      "linear-gradient(135deg, #f093fb, #f5576c)",
      "linear-gradient(135deg, #4facfe, #00f2fe)",
      "linear-gradient(135deg, #43e97b, #38f9d7)",
      "linear-gradient(135deg, #fa709a, #fee140)",
      "linear-gradient(135deg, #a8edea, #fed6e3)",
      "linear-gradient(135deg, #ffecd2, #fcb69f)",
      "linear-gradient(135deg, #ff8a80, #ff5722)",
      "linear-gradient(135deg, #ce9ffc, #7367f0)",
    ];
    return colors[index % colors.length];
  };



  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  };

  const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone;
  };

  if (isLoading || isFetching) {
    return (
      <div className="flex justify-center items-center h-[30rem]">
        <PulseLoader />
      </div>
    );
  }

  if (isSuccess && doctors.length === 0) {
    return (
      <div className="flex justify-center items-center flex-col min-h-[300px]">
        <img
          src="/Images/doctortable.png"
          className="w-[400px] h-auto mb-4"
          alt="No Doctors"
        />
        <p className="text-muted mt-3">No Doctors Available</p>
      </div>
    );
  }

  return (
    <InfiniteScroll
      dataLength={doctors?.length}
      next={fetchNextData}
      hasMore={hasMore}
      loader={<h4>Loading...</h4>}
      endMessage={
        <p style={{ textAlign: "center" }}>
          <b>Yay! You have seen it all</b>
        </p>
      }
      scrollableTarget="scrollableDiv"
    >
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-9xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{fontSize:'20px'}}>
              Our Doctors
            </h1>
            <p className="text-gray-600">
              Meet our qualified healthcare professionals
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
            {doctors.map((doctor, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-visible mb-3"
              >
                <div className="absolute -top-5 left-4 z-20">
                  <div className="bg-white px-3 py-1 rounded-full shadow-md border border-gray-200">
                    <span className="text-xs font-semibold text-gray-700">
                      #{doctor.doctorID}
                    </span>
                  </div>
                </div>

                <div className="p-4 pt-6 relative flex flex-col h-full">
                  {/* Top: Avatar & Info */}
                  <div className="flex items-center space-x-3 mb-4">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg relative"
                      style={{ background: getInitialsBg(index) }}
                    >
                      <span className="text-white font-bold text-xl">
                        {doctor.fullName?.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate text-lg">
                        {doctor.fullName}
                      </h3>
                      <p className="text-sm text-blue-600 font-medium truncate">
                        {doctor.specialization}
                      </p>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mb-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        doctor.status
                      )}`}
                    >
                      <div
                        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${getStatusDotColor(
                          doctor.status
                        )}`}
                      ></div>
                      {doctor.status}
                    </span>
                  </div>

                  {/* Doctor Details */}
                  <div className="space-y-3 text-sm text-gray-600 flex-grow">
                    <div className="flex items-center gap-2">
                      <FiPhone className="text-gray-600 text-[14px]" />
                      <span>{formatPhoneNumber(doctor.phoneNumber)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <FiMapPin className="text-gray-600 text-[13px]" />
                      <span className="line-clamp-2 text-sm text-gray-600">
                        {doctor.office_ClinicAddress || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiCalendar className="text-gray-600 text-[14px]" />
                      <span>Joined: {formatDate(doctor.joinDate)}</span>
                    </div>
                  </div>

                  {/* Action Buttons - Stick to bottom */}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex ">
                    <button
                      onClick={() =>
                        navigate(`/DoctorProfile/${doctor?.userUniqueID}?from=${encodeURIComponent(
              location.pathname + location.search
            )}`)
                      }
                      className="gradient-dark-button flex-1 text-white font-semibold py-2.5 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 shadow-md"
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </InfiniteScroll>
  );
}

export default DoctorsGrid;
